import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Inventory, InventoryLevel } from "@/api/inventory/_models/Inventory";
import { formatDayTime } from "@/app/date.utils";
import { ListGridExpandedSection } from "@/components/list-grid/ListGridExpandedSection";
import { INVENTORY_LIST_SYSTEM_LABEL, toCin7Level, toThreePlLevelList } from "@/features/inventory/inventory-list/inventoryList.utils";

interface InventoryListExpandedStockProps {
  row: Inventory;
}

const toCount = (value: number | null): string => {
  if (value === null) return "—";
  return value.toLocaleString();
};

interface SystemBlockProps {
  name: string;
  onHand: string;
  colour?: string;
  lines: string[];
}

const SystemBlock = (props: SystemBlockProps) => {
  const { name, onHand, colour = "text.primary", lines } = props;

  return (
    <Box
      sx={{
        display: "grid",
        flex: "1 1 0",
        minWidth: 0,
        rowGap: 0.25,
      }}
    >
      <Typography
        color="text.secondary"
        sx={{ fontWeight: 600 }}
        variant="body2"
      >
        {name}
      </Typography>
      <Typography
        sx={{
          color: colour,
          fontSize: 28,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 700,
          lineHeight: 1.15,
        }}
      >
        {onHand}
      </Typography>
      {lines.map((line) => (
        <Typography
          color="text.secondary"
          key={line}
          noWrap
          variant="body2"
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
};

interface DifferencePillProps {
  cin7: InventoryLevel | undefined;
  threePl: InventoryLevel;
  isMissingInCin7: boolean;
}

const DifferencePill = (props: DifferencePillProps) => {
  const { cin7, threePl, isMissingInCin7 } = props;
  const difference = (threePl.onHand ?? 0) - (cin7?.onHand ?? 0);
  let label = "Match";
  let bgcolor = "surface.success";
  let color = "success.main";
  if (difference !== 0) {
    label = difference > 0 ? `+${difference.toLocaleString()}` : `−${Math.abs(difference).toLocaleString()}`;
    bgcolor = isMissingInCin7 ? "surface.error" : "surface.warning";
    color = isMissingInCin7 ? "error.main" : "warning.main";
  }

  return (
    <Box
      component="span"
      sx={{
        alignSelf: "center",
        bgcolor,
        borderRadius: 999,
        color,
        flexShrink: 0,
        fontSize: 13,
        fontVariantNumeric: "tabular-nums",
        fontWeight: 700,
        px: 1,
        py: 0.25,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

/**
 * An inventory row's on-hand comparison: Cin7's number, then each 3PL's with a
 * difference pill between ("Match", "+12", "−3"), each with when it synced.
 */
export const InventoryListExpandedStock = (props: InventoryListExpandedStockProps) => {
  const { row } = props;
  const cin7 = toCin7Level(row);
  const threePl = toThreePlLevelList(row);
  const isMissingInCin7 = row.status === "missingInCin7";

  let cin7Lines = ["No Cin7 location"];
  if (cin7) cin7Lines = [`Synced ${formatDayTime(cin7.syncedAt)}`];

  return (
    <Box className="InventoryListExpanded-stock">
      <ListGridExpandedSection title="On hand">
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "stretch" }}
        >
          <SystemBlock
            lines={cin7Lines}
            name="Cin7"
            onHand={toCount(cin7?.onHand ?? null)}
          />
          {threePl.length === 0 && (
            <SystemBlock
              colour="text.disabled"
              lines={["None at this location"]}
              name="3PL"
              onHand="—"
            />
          )}
          {threePl.map((level) => {
            const isDifferent = (level.onHand ?? 0) !== (cin7?.onHand ?? 0);
            let colour = "text.primary";
            if (isDifferent) colour = isMissingInCin7 ? "error.main" : "warning.main";
            return (
              <Stack
                direction="row"
                key={level.system}
                spacing={1.5}
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                }}
              >
                <DifferencePill
                  cin7={cin7}
                  isMissingInCin7={isMissingInCin7}
                  threePl={level}
                />
                <SystemBlock
                  colour={colour}
                  lines={[`Synced ${formatDayTime(level.syncedAt)}`]}
                  name={INVENTORY_LIST_SYSTEM_LABEL[level.system] ?? level.system}
                  onHand={toCount(level.onHand)}
                />
              </Stack>
            );
          })}
        </Stack>
      </ListGridExpandedSection>
    </Box>
  );
};
